import internalChatChannelsAPI from 'dashboard/api/internalChatChannels';
import messagesModule from './messages';
import draftsModule from './drafts';
import searchModule from './search';
import pollsModule from './polls';

const state = {
  channels: [],
  archivedChannels: [],
  categories: [],
  activeChannelId: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

const getters = {
  getChannels: s => s.channels.filter(c => !c.is_dm && !c.is_archived),
  getDMChannels: s => s.channels.filter(c => c.is_dm && !c.is_archived),
  // Bolinha vermelha do menu: não lidas de canais e conversas diretas,
  // sem contar os silenciados e os arquivados.
  getTotalUnread: s =>
    s.channels
      .filter(c => !c.is_archived && !c.muted)
      .reduce((total, c) => total + (c.unread_count || 0), 0),
  getFavoriteChannels: s =>
    s.channels.filter(c => c.is_favorited && !c.is_archived),
  getArchivedChannels: s => s.archivedChannels,
  getCategories: s => s.categories,
  getUIFlags: s => s.uiFlags,
  getActiveChannelId: s => s.activeChannelId,
  getChannelById: s => id =>
    s.channels.find(c => c.id === id) ||
    s.archivedChannels.find(c => c.id === id) ||
    null,
  getChannelsByCategory: s => categoryId =>
    s.channels.filter(
      c => !c.is_dm && !c.is_archived && c.category_id === categoryId
    ),
};

const mutations = {
  SET_UI_FLAGS(s, flags) {
    s.uiFlags = { ...s.uiFlags, ...flags };
  },
  SET_CHANNELS(s, channels) {
    s.channels = channels;
  },
  SET_ARCHIVED_CHANNELS(s, channels) {
    s.archivedChannels = channels;
  },
  SET_CATEGORIES(s, categories) {
    s.categories = categories;
  },
  SET_ACTIVE_CHANNEL(s, id) {
    s.activeChannelId = id;
  },
  ADD_CHANNEL(s, channel) {
    if (s.channels.find(c => c.id === channel.id)) return;
    s.channels = [...s.channels, channel];
  },
  UPDATE_CHANNEL(s, channel) {
    s.channels = s.channels.map(c =>
      c.id === channel.id ? { ...c, ...channel } : c
    );
    s.archivedChannels = s.archivedChannels.map(c =>
      c.id === channel.id ? { ...c, ...channel } : c
    );
  },
  DELETE_CHANNEL(s, id) {
    s.channels = s.channels.filter(c => c.id !== id);
    s.archivedChannels = s.archivedChannels.filter(c => c.id !== id);
  },
};

const actions = {
  async get({ commit }) {
    commit('SET_UI_FLAGS', { isFetching: true });
    try {
      const { data } = await internalChatChannelsAPI.getWithParams({});
      const payload = data?.data || data || {};
      // Backend returns array directly; object format also supported for future
      const channels = Array.isArray(payload)
        ? payload
        : payload.channels || [];
      const categories = Array.isArray(payload) ? [] : payload.categories || [];
      commit('SET_CHANNELS', channels);
      commit('SET_CATEGORIES', categories);
    } finally {
      commit('SET_UI_FLAGS', { isFetching: false });
    }
  },

  async show({ commit }, channelId) {
    const { data } = await internalChatChannelsAPI.show(channelId);
    const channel = data?.data || data;
    if (channel) commit('UPDATE_CHANNEL', channel);
    return channel;
  },

  async create({ commit }, payload) {
    commit('SET_UI_FLAGS', { isCreating: true });
    try {
      const { data } = await internalChatChannelsAPI.create(payload);
      const channel = data?.data || data;
      commit('ADD_CHANNEL', channel);
      return channel;
    } finally {
      commit('SET_UI_FLAGS', { isCreating: false });
    }
  },

  async update({ commit }, { channelId, ...payload }) {
    commit('SET_UI_FLAGS', { isUpdating: true });
    try {
      const { data } = await internalChatChannelsAPI.update(channelId, payload);
      const channel = data?.data || data;
      commit('UPDATE_CHANNEL', channel);
      return channel;
    } finally {
      commit('SET_UI_FLAGS', { isUpdating: false });
    }
  },

  updateChannel({ commit }, channel) {
    commit('UPDATE_CHANNEL', channel);
  },

  async delete({ commit }, channelId) {
    commit('SET_UI_FLAGS', { isDeleting: true });
    try {
      await internalChatChannelsAPI.delete(channelId);
      commit('DELETE_CHANNEL', channelId);
    } finally {
      commit('SET_UI_FLAGS', { isDeleting: false });
    }
  },

  async archive({ commit }, channelId) {
    await internalChatChannelsAPI.archive(channelId);
    commit('UPDATE_CHANNEL', { id: channelId, is_archived: true });
  },

  async unarchive({ commit }, channelId) {
    await internalChatChannelsAPI.unarchive(channelId);
    commit('UPDATE_CHANNEL', { id: channelId, is_archived: false });
  },

  async fetchArchived({ commit }) {
    const { data } = await internalChatChannelsAPI.getWithParams({
      archived: true,
    });
    const payload = data?.data || data || {};
    const channels = Array.isArray(payload) ? payload : payload.channels || [];
    commit('SET_ARCHIVED_CHANNELS', channels);
  },

  async toggleFavorite({ commit }, channelId) {
    const { data } = await internalChatChannelsAPI.update(channelId, {
      toggle_favorite: true,
    });
    const channel = data?.data || data;
    commit('UPDATE_CHANNEL', channel);
  },

  async toggleMute({ commit }, channelId) {
    const { data } = await internalChatChannelsAPI.update(channelId, {
      toggle_mute: true,
    });
    const channel = data?.data || data;
    commit('UPDATE_CHANNEL', channel);
  },

  async createCategory({ commit }, { name, description }) {
    const { data } = await internalChatChannelsAPI.createCategory({
      name,
      description,
    });
    const category = data?.data || data;
    // refresh categories from the channel list response shape
    if (category) {
      commit('SET_CATEGORIES', [...state.categories, category]);
    }
    return category;
  },

  async markRead({ commit }, channelId) {
    // Zera na hora, sem esperar a API, para a bolinha do menu acompanhar.
    commit('UPDATE_CHANNEL', {
      id: channelId,
      unread_count: 0,
      has_unread_mention: false,
    });
    await internalChatChannelsAPI.markRead(channelId);
  },

  setActiveChannel({ commit }, id) {
    commit('SET_ACTIVE_CHANNEL', id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  modules: {
    messages: messagesModule,
    drafts: draftsModule,
    search: searchModule,
    polls: pollsModule,
  },
};
